<!--
  The saved Contract Reports.

  A list and a "New contract report" button, nothing else: the sheet itself
  is the form on the next screen. Follows DailyReportList.svelte's structure
  exactly — same per-site `?site=` filter, same newest-first sort, same empty
  state — except a contract report carries no `project` text (there is no
  such column on this record; the site link is what ties it to a job), and a
  cancelled day is the thing a reader scans for, so it gets its own pill.

  Per-site: with `?site=`, this is one site's reports, newest first — the
  grouped view lives on the site page (SiteForm.svelte), this screen is just
  its "All contract reports" drill-down. Without a site, every report across
  every site, flat, newest first.
-->
<script lang="ts">
  import { linkHandler, location, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import { contractReports, contractReportsLoading, loadContractReports } from '../../lib/store';

  const siteId = $derived(siteIdFromQuery($location.query));

  $effect(() => {
    void loadContractReports(siteId);
  });

  /** Newest by the record's own date, falling back to when it was last touched. */
  const sorted = $derived(
    [...$contractReports].sort((a, b) =>
      a.report_date === b.report_date
        ? b.updated_at.localeCompare(a.updated_at)
        : b.report_date.localeCompare(a.report_date)
    )
  );
</script>

<div class="card">
  <div class="card__body">
    {#if siteId !== null}
      <a class="btn btn--sm" href={routePath.qualitySite(siteId)} onclick={linkHandler}>&#8592; Back to site</a>
    {:else}
      <a class="btn btn--sm" href={routePath.quality()} onclick={linkHandler}>&#8592; All sites</a>
    {/if}
    <h1 class="card__title">Contract Report</h1>
    <button
      class="btn btn--primary btn--block"
      type="button"
      onclick={() => navigate(routePath.newQualityContractReport(siteId ?? undefined))}
    >
      New contract report
    </button>
  </div>
</div>

{#if $contractReportsLoading && $contractReports.length === 0}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if sorted.length === 0}
  <div class="card">
    <div class="card__body">
      <p class="meta">
        No contract reports saved yet. The first one starts from a blank equipment and operator sheet.
      </p>
    </div>
  </div>
{:else}
  <div class="card">
    <div class="card__body">
      <ul class="qlist">
        {#each sorted as record (record.id)}
          <li>
            <a href={routePath.qualityContractReport(record.id)} onclick={linkHandler}>
              <span class="qlist__head">
                <strong>{record.report_date || 'No date recorded'}</strong>
                {#if record.cancelled === 1}<span class="pill pill--hold">Cancelled</span>{/if}
              </span>
              <span class="meta">{record.ticket_no ? `Ticket ${record.ticket_no}` : 'No ticket number'}</span>
            </a>
          </li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

<style>
  .qlist {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .qlist li + li {
    border-top: 1px solid var(--line-soft);
  }

  .qlist a {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.85rem 0;
    color: inherit;
    text-decoration: none;
    /* Tapped with gloves on, same as the field screens. */
    min-height: var(--tap);
  }

  .qlist a:hover strong {
    text-decoration: underline;
  }

  .qlist__head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
</style>
